import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { ArrowLeft, CalendarBlank, ArrowUpRight } from "@phosphor-icons/react";

const useBlogLang = () => {
  const [lang, setLangState] = useState(() => localStorage.getItem("otelya_blog_lang") || localStorage.getItem("otelya_lang") || "it");
  const setLang = (l) => { localStorage.setItem("otelya_blog_lang", l); setLangState(l); };
  return [lang, setLang];
};

const I18N = {
  it: {
    tag: "Otelya Journal",
    title1: "Storie di", titleEm: "ospitalità", title2: ", esperienze, territorio.",
    subtitle: "Il diario editoriale di Otelya. Ogni settimana nuovi articoli su accoglienza premium, comunicazione con gli ospiti e cultura dell'ospitalità italiana.",
    read: "Leggi l'articolo", empty: "Nessun articolo pubblicato ancora. Torna presto — il primo arriverà lunedì mattina.",
    loading: "Caricamento…", all: "Tutti gli articoli",
    footer: "Journal. Contenuti generati con cura dall'AI editoriale di Otelya.",
    notFound: "Articolo non trovato.",
    dateLocale: "it-IT",
  },
  en: {
    tag: "Otelya Journal",
    title1: "Stories of", titleEm: "hospitality", title2: ", experiences, places.",
    subtitle: "The Otelya editorial diary. Every week new articles on premium hospitality, guest communication and the culture of Italian welcome.",
    read: "Read the article", empty: "No articles published yet. Come back soon — the first one is on its way.",
    loading: "Loading…", all: "All articles",
    footer: "Journal. Editorial content curated by Otelya's AI.",
    notFound: "Article not found.",
    dateLocale: "en-GB",
  },
};

function BlogNav({ lang, setLang, showLang = true }) {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-canvas/80 border-b border-line/70">
      <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/"><Logo /></Link>
        <div className="flex items-center gap-3">
          {showLang && (
            <div className="flex items-center gap-0 text-xs" data-testid="blog-lang-toggle">
              <button onClick={()=>setLang("it")} data-testid="blog-lang-it"
                className={`px-2.5 py-1 rounded-full transition ${lang==="it"?"bg-ink text-canvas":"text-stone hover:text-ink"}`}>IT</button>
              <button onClick={()=>setLang("en")} data-testid="blog-lang-en"
                className={`px-2.5 py-1 rounded-full transition ${lang==="en"?"bg-ink text-canvas":"text-stone hover:text-ink"}`}>EN</button>
            </div>
          )}
          <Link to="/" className="text-sm text-stone hover:text-ink flex items-center gap-1" data-testid="blog-back-home">
            <ArrowLeft size={14}/> Otelya
          </Link>
        </div>
      </div>
    </header>
  );
}

export function BlogIndex() {
  const [lang, setLang] = useBlogLang();
  const [posts, setPosts] = useState(null);
  const t = I18N[lang];
  useEffect(() => {
    setPosts(null);
    api.get(`/blog/posts?lang=${lang}`).then(r => setPosts(r.data));
  }, [lang]);

  const seoTitle = lang === "en"
    ? "Otelya Journal · Stories of Italian hospitality"
    : "Otelya Journal · Storie di ospitalità italiana";
  const seoDesc = lang === "en"
    ? "Weekly editorial articles on premium hospitality, guest communication and Italian welcome culture. Curated by Otelya's AI editor."
    : "Ogni settimana nuovi articoli editoriali su accoglienza premium, comunicazione con gli ospiti e cultura dell'ospitalità italiana. A cura dell'editoriale AI di Otelya.";

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Helmet>
        <html lang={lang} />
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href="https://otelya.app/blog" />
        <link rel="alternate" hrefLang="it-IT" href="https://otelya.app/blog" />
        <link rel="alternate" hrefLang="en-US" href="https://otelya.app/blog?lang=en" />
        <link rel="alternate" hrefLang="x-default" href="https://otelya.app/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content="https://otelya.app/blog" />
        <meta property="og:locale" content={lang === "en" ? "en_US" : "it_IT"} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
      </Helmet>
      <BlogNav lang={lang} setLang={setLang} />
      <main className="max-w-4xl mx-auto px-6 py-16 lg:py-24">
        <div className="mb-14">
          <div className="text-xs uppercase tracking-widest text-stone" data-testid="blog-tag">{t.tag}</div>
          <h1 className="font-serif text-4xl lg:text-5xl mt-3 text-ink leading-tight">
            {t.title1} <em className="italic text-terracotta">{t.titleEm}</em>{t.title2}
          </h1>
          <p className="mt-4 text-stone leading-relaxed max-w-2xl">{t.subtitle}</p>
        </div>

        {!posts && <div className="text-stone">{t.loading}</div>}
        {posts?.length === 0 && (
          <div className="text-stone italic py-10 text-center border border-dashed border-line rounded-2xl">
            {t.empty}
          </div>
        )}

        <div className="space-y-10" data-testid="blog-list">
          {posts?.map(p => (
            <Link key={p.id} to={`/blog/${p.slug}`} className="block group" data-testid={`blog-card-${p.slug}`}>
              <article className="py-8 border-b border-line last:border-0">
                {p.hero_image_url && (
                  <div className="mb-6 aspect-[16/9] rounded-2xl overflow-hidden bg-gradient-to-br from-terracotta/20 via-gold/10 to-olive/15 relative flex items-center justify-center">
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <div className="text-terracotta/60 font-serif text-4xl italic">Otelya</div>
                      <div className="text-stone/60 text-[10px] uppercase tracking-widest mt-2">Journal</div>
                    </div>
                    <img
                      src={p.hero_image_url.startsWith("http") ? p.hero_image_url : `${process.env.REACT_APP_BACKEND_URL}${p.hero_image_url}`}
                      alt={p.title}
                      loading="lazy"
                      onError={(e) => { if (e && e.currentTarget) { e.currentTarget.style.opacity = '0'; } }}
                      className="relative w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      data-testid={`blog-hero-${p.slug}`}
                    />
                  </div>
                )}
                <div className="text-xs uppercase tracking-widest text-stone flex items-center gap-2">
                  <CalendarBlank size={12}/> {new Date(p.published_at).toLocaleDateString(t.dateLocale, {day:'numeric', month:'long', year:'numeric'})}
                </div>
                <h2 className="font-serif text-2xl lg:text-3xl mt-3 text-ink leading-snug group-hover:text-terracotta transition-colors">
                  {p.title}
                </h2>
                <p className="mt-3 text-stone leading-relaxed line-clamp-3">
                  {(p.content || '').replace(/^#\s+.+$/m, '').replace(/[#*_`]/g, '').trim().slice(0, 220)}…
                </p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-ink font-medium">
                  {t.read} <ArrowUpRight weight="bold" size={14}/>
                </div>
              </article>
            </Link>
          ))}
        </div>
      </main>
      <footer className="max-w-4xl mx-auto px-6 py-10 text-xs text-stone text-center border-t border-line">
        © {new Date().getFullYear()} Otelya — {t.footer}
      </footer>
    </div>
  );
}

export function BlogArticle() {
  const { slug } = useParams();
  const [lang, setLang] = useBlogLang();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get(`/blog/posts/${slug}`).then(r => {
      setPost(r.data);
      if (r.data?.language && r.data.language !== lang) setLang(r.data.language);
    }).catch(() => setError(true));
    // eslint-disable-next-line
  }, [slug]);
  const t = I18N[lang] || I18N.it;

  // Build article-specific SEO
  const articleLang = post?.language || lang;
  const canonical = `https://otelya.app/blog/${slug}`;
  const heroAbs = post?.hero_image_url
    ? (post.hero_image_url.startsWith("http")
        ? post.hero_image_url
        : `https://otelya.app${post.hero_image_url}`)
    : "https://otelya.app/og-image-otelya.jpg";
  const rawExcerpt = post?.content
    ? post.content.replace(/^#\s+.+$/m, "").replace(/[#*_`]/g, "").trim().slice(0, 160)
    : "";
  const seoDesc = rawExcerpt || (articleLang === "en"
    ? "An editorial article from the Otelya Journal on premium Italian hospitality."
    : "Un articolo editoriale dal Journal di Otelya sull'ospitalità italiana premium.");
  const seoTitle = post ? `${post.title} · Otelya Journal` : (articleLang === "en" ? "Otelya Journal" : "Otelya Journal");
  const ldJson = post ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": canonical + "#article",
    "headline": post.title,
    "description": seoDesc,
    "image": [heroAbs],
    "datePublished": post.published_at,
    "dateModified": post.published_at,
    "inLanguage": articleLang === "en" ? "en-US" : "it-IT",
    "url": canonical,
    "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
    "author": { "@type": "Organization", "name": "Otelya", "url": "https://otelya.app/" },
    "publisher": {
      "@type": "Organization",
      "name": "Otelya",
      "logo": { "@type": "ImageObject", "url": "https://otelya.app/otelya-icon-512.png" }
    }
  } : null;

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Helmet>
        <html lang={articleLang} />
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post?.title || seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:image" content={heroAbs} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={articleLang === "en" ? "en_US" : "it_IT"} />
        {post?.published_at && <meta property="article:published_time" content={post.published_at} />}
        <meta property="article:publisher" content="https://otelya.app/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post?.title || seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        <meta name="twitter:image" content={heroAbs} />
        {ldJson && <script type="application/ld+json">{JSON.stringify(ldJson).replace(/</g, "\\u003c")}</script>}
      </Helmet>
      <BlogNav lang={lang} setLang={setLang} showLang={false} />
      <main className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-stone hover:text-ink mb-8" data-testid="blog-back">
          <ArrowLeft size={14}/> {t.all}
        </Link>
        {error && <div className="text-stone italic">{t.notFound}</div>}
        {!post && !error && <div className="text-stone">{t.loading}</div>}
        {post && (
          <article data-testid="blog-article">
            {post.hero_image_url && (
              <div className="mb-10 aspect-[16/9] rounded-2xl overflow-hidden bg-gradient-to-br from-terracotta/20 via-gold/10 to-olive/15 relative flex items-center justify-center">
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="text-terracotta/60 font-serif text-5xl italic">Otelya</div>
                  <div className="text-stone/60 text-xs uppercase tracking-widest mt-2">Journal</div>
                </div>
                <img
                  src={post.hero_image_url.startsWith("http") ? post.hero_image_url : `${process.env.REACT_APP_BACKEND_URL}${post.hero_image_url}`}
                  alt={post.title}
                  onError={(e) => { if (e && e.currentTarget) { e.currentTarget.style.opacity = '0'; } }}
                  className="relative w-full h-full object-cover"
                  data-testid="blog-article-hero"
                />
              </div>
            )}
            <div className="text-xs uppercase tracking-widest text-stone flex items-center gap-2">
              <CalendarBlank size={12}/> {new Date(post.published_at).toLocaleDateString(t.dateLocale, {day:'numeric', month:'long', year:'numeric'})}
              {post.language && <span className="ml-2 px-2 py-0.5 rounded-full bg-sand text-[10px] uppercase tracking-widest">{post.language}</span>}
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl mt-4 text-ink leading-[1.1]">
              {post.title}
            </h1>
            <div
              className="prose mt-10 max-w-none prose-headings:font-serif prose-headings:text-ink prose-h1:hidden prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-p:text-stone prose-p:leading-relaxed prose-a:text-terracotta prose-strong:text-ink"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content_html || '', { USE_PROFILES: { html: true } }) }}
            />
            {post.related_articles && post.related_articles.length > 0 && (
              <aside className="mt-16 pt-10 border-t border-line" data-testid="blog-related">
                <div className="text-xs uppercase tracking-widest text-stone mb-4">Articoli correlati</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {post.related_articles.map(r => (
                    <a key={r.slug} href={`/blog/${r.slug}`} className="block rounded-xl border border-line bg-canvas hover:border-terracotta transition p-3 group" data-testid={`related-${r.slug}`}>
                      {r.hero_image_url && (
                        <div className="aspect-video rounded-lg overflow-hidden mb-3">
                          <img src={r.hero_image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
                        </div>
                      )}
                      <div className="font-serif text-base text-ink leading-tight line-clamp-2">{r.title}</div>
                      {r.published_at && (
                        <div className="text-[11px] text-stone mt-2">{new Date(r.published_at).toLocaleDateString("it-IT", {day: "numeric", month: "long", year: "numeric"})}</div>
                      )}
                    </a>
                  ))}
                </div>
              </aside>
            )}
          </article>
        )}
      </main>
      <footer className="max-w-3xl mx-auto px-6 py-10 text-xs text-stone text-center border-t border-line">
        © {new Date().getFullYear()} Otelya — Journal
      </footer>
    </div>
  );
}
